import { memo } from 'react';
import { Label, RadioGroup, RadioGroupItem } from '@flote/ui';
import { Package, Truck, ArrowRightLeft } from 'lucide-react';

export type TaskCreationType = 'dropoff' | 'pickup' | 'linked';

interface TaskTypeSelectorProps {
  value: TaskCreationType;
  onValueChange: (value: TaskCreationType) => void;
  disabled?: boolean;
}

/**
 * Task Type Selector Component
 *
 * Radio button group for selecting task creation type:
 * - Dropoff Only (default) - Standard delivery to customer
 * - Pickup Only - Standalone pickup from supplier
 * - Pickup + Dropoff - Linked pair with dependency enforcement
 */
export const TaskTypeSelector = memo<TaskTypeSelectorProps>(
  ({ value, onValueChange, disabled = false }) => {
    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium">Task Type</Label>
        <RadioGroup
          value={value}
          onValueChange={(v) => onValueChange(v as TaskCreationType)}
          className="grid gap-3 sm:grid-cols-3"
          disabled={disabled}
        >
          {/* Dropoff Only */}
          <Label
            htmlFor="type-dropoff"
            className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors ${
              value === 'dropoff'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <RadioGroupItem value="dropoff" id="type-dropoff" />
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Dropoff Only</p>
                <p className="text-xs text-muted-foreground">Deliver to customer</p>
              </div>
            </div>
          </Label>

          {/* Pickup Only */}
          <Label
            htmlFor="type-pickup"
            className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors ${
              value === 'pickup'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <RadioGroupItem value="pickup" id="type-pickup" />
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Pickup Only</p>
                <p className="text-xs text-muted-foreground">Pick up from supplier</p>
              </div>
            </div>
          </Label>

          {/* Linked Pickup + Dropoff */}
          <Label
            htmlFor="type-linked"
            className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors ${
              value === 'linked'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <RadioGroupItem value="linked" id="type-linked" />
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Pickup + Dropoff</p>
                <p className="text-xs text-muted-foreground">Linked task pair</p>
              </div>
            </div>
          </Label>
        </RadioGroup>
        <p className="text-xs text-muted-foreground">
          {value === 'linked'
            ? 'Creates linked pickup and dropoff tasks. Dropoff will be blocked until pickup is completed.'
            : value === 'pickup'
              ? 'Creates a standalone pickup task from a supplier location.'
              : 'Creates a standard delivery task to a customer.'}
        </p>
      </div>
    );
  }
);

TaskTypeSelector.displayName = 'TaskTypeSelector';
